/** QuerySearch namespaces for the Find & Replace page.
 *
 * Operators per namespace are a SUBSET of the spec's `x-namespace-operators` on
 * `ContentFilterNode` (contract-tested in contentNamespaces.contract.test.ts).
 * `exact` is deliberately omitted wherever `iexact` exists — the DSL serialises
 * both to `=`, which re-parses to `iexact`, so an `exact` chip could not
 * round-trip through the URL.
 *
 * The `in=` scope values are duplicated here as autocomplete labels only; the
 * authoritative list comes from `GET /content_search/slots/` and is injected at
 * runtime by `buildContentNamespaces(scopes)`. A hardcoded fallback keeps the
 * widget usable if that request is still in flight.
 */
import type { NamespaceConfigMap } from '@/components/QuerySearch';

import { makeSuggestionFetcher } from '@/api/notifications';

/** Used until `GET /slots/` resolves. Kept deliberately short — it is a
 *  fallback, not a second source of truth. */
const FALLBACK_SCOPES = [
  'all',
  'messages',
  'email',
  'sms',
  'whatsapp',
  'chat',
  'blocks',
  'themes',
  'policy_rules',
  'rules',
  'tag_attributes',
  'strings',
  'standard_templates',
];

function localLabels(labels: string[]) {
  return (query: string) =>
    Promise.resolve(
      labels
        .filter((s) => s.toLowerCase().includes(query.toLowerCase()))
        .map((s) => ({ value: s, label: s })),
    );
}

export function buildContentNamespaces(scopeValues?: string[]): NamespaceConfigMap {
  const scopes = scopeValues?.length ? scopeValues : FALLBACK_SCOPES;
  return {
    text: {
      label: 'text',
      operators: ['icontains'],
      valueType: 'string',
      help: 'Literal text to find (at least 3 characters). Several `text` terms must ALL appear somewhere in the record. Quote fragments containing spaces or symbols.',
      examples: ['text=~Versailes', 'text=~"{% if-ruleset"', 'text=~participant.tag_context.guide'],
    },
    re: {
      label: 're',
      operators: ['icontains'],
      valueType: 'string',
      help: 'Python regular expression, evaluated on the server. Needs something the database can narrow on first — either a literal the pattern guarantees, or a tenant/tour/codename filter. Patterns with alternation or groups cannot be narrowed automatically.',
      examples: [String.raw`re=~"participant\.\w+\.pickup"`, String.raw`re=~"\d{4}-\d{2}-\d{2}"`],
    },
    in: {
      label: 'in',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localLabels(scopes),
      help: 'Where to search. Repeat to widen. Defaults to everything.',
      examples: ['in=messages', 'in=blocks', 'in=tag_attributes'],
    },
    attr: {
      label: 'attr',
      operators: ['iexact'],
      valueType: 'string',
      help: 'Restrict JSON fields (tag attributes, channel overrides, theme tokens) to one attribute name.',
      examples: ['attr=vendor_prepayment_text', 'attr=meet1_name'],
    },
    lang: {
      label: 'lang',
      operators: ['iexact'],
      valueType: 'string',
      help: 'Restrict multilingual values and translated strings to one ISO language code.',
      examples: ['lang=pt', 'lang=fr'],
    },
    tenant: {
      label: 'tenant',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: makeSuggestionFetcher('tenant'),
      help: 'Tour operator by code; an all-digits value also matches the tenant id. Use "system" for system-wide records.',
      examples: ['tenant=curatedplanet', 'tenant=system'],
    },
    tour: {
      label: 'tour',
      operators: ['exact'],
      valueType: 'integer',
      help: 'Only records scoped to this specific tour (Group_Tour id). Kinds that have no tour dimension drop out of the search.',
      examples: ['tour=8123'],
    },
    event: {
      label: 'event',
      operators: ['iexact', 'icontains'],
      valueType: 'string',
      help: 'Event code of the owning notification.',
      examples: ['event=tour_participant.created', 'event=~departure'],
    },
    enabled: {
      label: 'enabled',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localLabels(['yes', 'no']),
      help: 'Whether the owning record is live (yes/no).',
      examples: ['enabled=no'],
    },
    method: {
      label: 'method',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localLabels([
        'Email',
        'SMS',
        'WhatsApp',
        'Airbnb',
        'Chat message',
        'Push notification',
        'Sentry alert',
        'SNS topic',
        'HTTP request',
      ]),
      help: 'Delivery channel. Narrows to delivery methods and standard templates; other kinds drop out.',
      examples: ['method=email', 'method=whatsapp'],
    },
    category: {
      label: 'category',
      operators: ['iexact', 'icontains'],
      valueType: 'string',
      help: 'Tag category by code or name. Narrows to tags.',
      examples: ['category=pickup', 'category=~product'],
    },
    codename: {
      label: 'codename',
      operators: ['iexact', 'icontains'],
      valueType: 'string',
      help: 'Code name of the owning block, theme, policy, ruleset or string.',
      examples: ['codename=signature_html', 'codename=~meeting'],
    },
    updated: {
      label: 'updated',
      operators: ['exact', 'range'],
      valueType: 'datetime',
      help: 'When the record was last edited, UTC. `=` matches a whole day; a range takes date-time bounds.',
      examples: ['updated=2026-08-16', 'updated="2026-08-01 09:00".."2026-08-17 18:00"'],
    },
    created: {
      label: 'created',
      operators: ['exact', 'range'],
      valueType: 'datetime',
      help: 'When the record was created, UTC.',
      examples: ['created=2026-01-01..2026-07-01'],
    },
    id: {
      label: 'id',
      operators: ['exact'],
      valueType: 'integer',
      help: 'Record id, within whatever kinds the scope allows.',
      examples: ['id=40013'],
    },
  };
}
